import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/menu/header';
import Footer from '../../components/menu/footer';
import TournamentData from '../../components/iscrizioneForm/tournamentData';
import PlayerData from '../../components/iscrizioneForm/playerData';
import TeamData from '../../components/iscrizioneForm/teamData';
import API from '../../api/API';
import {
  verificaCodiceFiscale,
  CODICE_PASSEPARTOUT,
  CODICE_CIFRE_MINIME
} from '../../components/iscrizioneForm/utils/codiceFiscale';
import './iscrizioneForm.css';

const IscrizioneForm = () => {
  const navigate = useNavigate();
  
  // Tournament info
  const [torneo, setTorneo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Un no previsto, non un guasto: l'edizione e' stata avviata e il link non vale piu'. Il
  // messaggio lo scrive il server, qui si cambia solo il tono della schermata.
  const [iscrizioniChiuse, setIscrizioniChiuse] = useState(false);
  const [gruppiScout, setGruppiScout] = useState([]);
  const { torneoId, squadraHash } = useParams(); 
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(!!squadraHash);
  const [originalSquadraData, setOriginalSquadraData] = useState(null);
  const [squadraPreiscrizione, setSquadraPreiscrizione] = useState(false);

  // L'admin non e' piu' "chi ha un token in sessione": e' chi apre il link col codice che
  // l'accettazione ha emesso per questa squadra. Il privilegio si esercita chiedendolo, quindi
  // un organizzatore che apre il link nudo vede la pagina come la vede la squadra.
  const [searchParams] = useSearchParams();
  const codiceAdmin = searchParams.get('admin');
  // isAdmin governa solo cosa si disegna qui (banner, blocco lato client): non e' una garanzia di
  // sicurezza. Nella finestra fra chiusuraModifiche passata e Avvio non ancora impostato, il
  // server non guarda affatto chiusuraModifiche - e' deliberato - quindi in quella finestra un
  // ?admin= qualsiasi, anche inventato, toglie il blocco lato client. Non e' un buco nuovo (prima
  // bastava un token finto in localStorage) e non attraversa un confine vero: chi decide se la
  // scrittura passa davvero e' sempre il server, con Avvio/Chiusura/Vale.
  const isAdmin = !!codiceAdmin;

  const isEditDeadlinePassed = !!(
    isEditMode &&
    torneo?.chiusuraModifiche &&
    new Date() > new Date(torneo.chiusuraModifiche)
  );
  const isEditingBlocked = isEditDeadlinePassed && !isAdmin;

  // Team data
  const [teamData, setTeamData] = useState({
    maglia: '',
    contatto: '',
    logo: null,
    hash: '',
    accompagnatori: '',
    vegetariani: '',
    vegani: '',
    celiaci: '',
    altreIntolleranze: '',
    gruppoScout: null,
    gruppoScoutText: '',
  });
  
  // Players data
  const [players, setPlayers] = useState(() => {
    return Array.from({ length: 7 }, (_, index) => ({
      id: Date.now() + index,
      codice: '',
      codiceFiscale: '',
      nome: '',
      cognome: '',
      sesso: '',
      dataNascita: '',
      gruppo: '',
      maglia: '',
      numeroMaglia: ''
    }));
  });
  
  // Form validation
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false); 
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (squadraHash) {
      // Modalità modifica - carica dati squadra esistente
      fetchSquadraData();
    } else {
      // Modalità creazione - carica solo dati torneo
      fetchTournamentDetails();
    }
  }, [torneoId, squadraHash]);

  useEffect(() => {
    API.gruppo.getAll().then(res => {
      if (res.isSuccess) {
        setGruppiScout(res.data || []);
      }
    });
  }, []);

  /**
   * Il no che ferma il modulo. Il 409 e' quello previsto - l'edizione e' stata avviata, il link
   * non vale piu' - e allora si scrive il motivo che arriva dal server; qualunque altro esito e'
   * un guasto, e il messaggio del server (un "Errore 404" nudo) non aiuterebbe nessuno.
   */
  const mostraChiusura = (risposta, motivoGenerico) => {
    const chiuse = risposta.status === 409;
    setIscrizioniChiuse(chiuse);
    setError(chiuse && risposta.errorMsg ? risposta.errorMsg : motivoGenerico);
  };

  const fetchSquadraData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Prima carica i dati del torneo. Se l'edizione e' stata avviata risponde 409: il link di
      // modifica non vale piu', e il modulo non deve aprirsi con dentro i dati della squadra.
      const torneoResponse = await API.torneo.dettagliIscrizione(torneoId, codiceAdmin);
      if (!torneoResponse.isSuccess || !torneoResponse.data) {
        mostraChiusura(torneoResponse, 'Errore nel caricamento dei dettagli del torneo');
        return;
      }
      setTorneo(torneoResponse.data);

      // Poi carica i dati della squadra
      const squadraResponse = await API.squadra.getByHash(squadraHash, codiceAdmin);

      if (squadraResponse.isSuccess && squadraResponse.data) {
        const squadraData = squadraResponse.data;
        setOriginalSquadraData(squadraData);
        setSquadraPreiscrizione(squadraData.preiscrizione || false);

        // Precompila i dati della squadra
        setTeamData({
          maglia: squadraData.nome || '',
          contatto: squadraData.contatto || '',
          hash: squadraData.hash || '',
          logo: squadraData.logo ? {
            preview: `data:image/png;base64,${squadraData.logo}`,
            base64: squadraData.logo
          } : null,
          accompagnatori: squadraData.accompagnatori?.toString() || '',
          vegetariani: squadraData.vegetariani?.toString() || '',
          vegani: squadraData.vegani?.toString() || '',
          celiaci: squadraData.celiaci?.toString() || '',
          altreIntolleranze: squadraData.altreIntolleranze || '',
          gruppoScout: squadraData.gruppoScoutId || null,
          gruppoScoutText: squadraData.gruppoScout || '',
        });

        // Precompila i dati dei giocatori
        if (squadraData.giocatori && squadraData.giocatori.length > 0) {
          const playersData = squadraData.giocatori.map((giocatore, index) => ({
            id: giocatore.id || Date.now() + index,
            codice: giocatore.cod?.toString() || '',
            codiceFiscale: giocatore.cf || '',
            nome: giocatore.nome || '',
            cognome: giocatore.cognome || '',
            sesso: giocatore.sesso || '',
            dataNascita: giocatore.dataDiNascita || '',
            gruppo: giocatore.gruppo || '',
            maglia: giocatore.maglia || '',
            numeroMaglia: giocatore.n?.toString() || '',
            isNew: false // Giocatori esistenti
          }));
          setPlayers(playersData);
        }
        
        setIsEditMode(true);
      } else {
        mostraChiusura(squadraResponse, 'Squadra non trovata o hash non valido');
      }
    } catch (error) {
      console.error('Errore nel caricamento della squadra:', error);
      setError('Errore di connessione nel caricamento della squadra');
    } finally {
      setLoading(false);
    }
  };

  const fetchTournamentDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Qui niente codiceAdmin: questa rotta (senza hash) crea una squadra nuova, e la creazione
      // resta fuori dalla finestra dell'admin anche sul server. Passarlo aprirebbe il modulo su
      // un'edizione avviata per poi vedere il salvataggio respinto - peggio che non aprirlo.
      const response = await API.torneo.dettagliIscrizione(torneoId);

      if (response.isSuccess && response.data) {
        setTorneo(response.data);
        setIsEditMode(false);
      } else {
        mostraChiusura(response, 'Errore nel caricamento dei dettagli del torneo');
      }
    } catch (error) {
      console.error('Errore nel caricamento del torneo:', error);
      setError('Errore di connessione nel caricamento del torneo');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamDataChange = (field, value) => {
    setTeamData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/png')) {
      setErrors(prev => ({
        ...prev,
        logo: 'Il logo deve essere un file PNG'
      }));
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        logo: 'Il logo deve essere massimo 2MB'
      }));
      return;
    }

    // Validate image dimensions
    const img = new Image();
    img.onload = () => {
      if (img.width > 800 || img.height > 800) {
        setErrors(prev => ({
          ...prev,
          logo: 'Il logo deve essere massimo 800x800 pixel'
        }));
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        setTeamData(prev => ({
          ...prev,
          logo: {
            file: file,
            preview: e.target.result,
            base64: e.target.result.split(',')[1]
          }
        }));
        
        setErrors(prev => ({
          ...prev,
          logo: null
        }));
      };
      reader.readAsDataURL(file);
    };

    img.onerror = () => {
      setErrors(prev => ({
        ...prev,
        logo: 'File immagine non valido'
      }));
    };

    img.src = URL.createObjectURL(file);
  };

  const handlePlayerCodeBlur = async (playerId, codice) => {
    if (!codice || !codice.trim()) return;
    
    const cleanCodice = codice.trim();
    
    // Verifica che sia un numero valido
    if (isNaN(parseInt(cleanCodice))) return;
    
    try {
      const response = await API.giocatore.getByCodice(parseInt(cleanCodice));
      
      if (response.isSuccess && response.data) {
        const giocatoreData = response.data;
        
        // Aggiorna i dati del giocatore se trovato
        setPlayers(prev => prev.map(player => 
          player.id === playerId 
            ? {
                ...player,
                nome: giocatoreData.nome || player.nome,
                cognome: giocatoreData.cognome || player.cognome,
                sesso: giocatoreData.sesso || player.sesso,
                dataNascita: giocatoreData.dataDiNascita || player.dataNascita,
                gruppo: giocatoreData.gruppo || player.gruppo,
                maglia: giocatoreData.maglia || player.maglia,
                numeroMaglia: giocatoreData.n || player.numeroMaglia,
                codiceFiscale: giocatoreData.cf || player.codiceFiscale
              }
            : player
        ));
        
        // Rimuovi eventuali errori relativi a questi campi per questo giocatore
        const fieldsToReset = ['nome', 'cognome', 'sesso', 'dataNascita', 'gruppo', 'maglia', 'numeroMaglia'];
        setErrors(prev => {
          const newErrors = { ...prev };
          fieldsToReset.forEach(field => {
            const errorKey = `player_${playerId}_${field}`;
            if (newErrors[errorKey]) {
              delete newErrors[errorKey];
            }
          });
          return newErrors;
        });
      }
    } catch (error) {
      console.debug('Errore nel recupero dati giocatore:', error);
    }
  };

  const removeLogoHandler = () => {
    setTeamData(prev => ({
      ...prev,
      logo: null
    }));
  };

  const addPlayer = () => {
    const newPlayer = {
      id: Date.now(),
      codice: '',
      codiceFiscale: '',
      nome: '',
      cognome: '',
      sesso: '',
      dataNascita: '',
      gruppo: '',
      maglia: '',
      numeroMaglia: '',
      isNew: true // Flag per identificare giocatori nuovi
    };
    setPlayers(prev => [...prev, newPlayer]);
  };

  const removePlayer = (playerId) => {
    setPlayers(prev => prev.filter(p => p.id !== playerId));
  };

  const updatePlayer = (playerId, field, value) => {
    const player = players.find(p => p.id === playerId);

    // In modalità modifica, per giocatori esistenti:
    // - Se preiscrizione=true: permetti modifica di tutti i campi
    // - Se preiscrizione=false: permetti solo modifica di maglia e numeroMaglia
    if (isEditMode && player && !player.isNew && !squadraPreiscrizione && field !== 'maglia' && field !== 'numeroMaglia') {
      return;
    }

    setPlayers(prev => prev.map(player =>
      player.id === playerId
        ? { ...player, [field]: value }
        : player
    ));

    // Clear field error
    const errorKey = `player_${playerId}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate team data
    if (!teamData.maglia.trim()) {
      newErrors.maglia = 'Nome squadra obbligatorio';
    }
    
    if (!teamData.contatto.trim()) {
      newErrors.contatto = 'Contatto obbligatorio';
    } else if (!/^[+]?[0-9\s\-\(\)]{10,}$/.test(teamData.contatto)) {
      newErrors.contatto = 'Formato telefono non valido';
    }

    if (!teamData.gruppoScout) {
      newErrors.gruppoScout = 'Gruppo scout obbligatorio';
    }

    // Validate accompagnatori
    if (teamData.accompagnatori && teamData.accompagnatori.trim()) {
      const accompagnatori = parseInt(teamData.accompagnatori);
      if (isNaN(accompagnatori) || accompagnatori < 0 || accompagnatori > 10) {
        newErrors.accompagnatori = 'Numero accompagnatori deve essere tra 0 e 10';
      }
    }

    // Validate vegetariani
    if (teamData.vegetariani && teamData.vegetariani.trim()) {
      const vegetariani = parseInt(teamData.vegetariani);
      if (isNaN(vegetariani) || vegetariani < 0 || vegetariani > 20) {
        newErrors.vegetariani = 'Numero vegetariani deve essere tra 0 e 20';
      }
    }

    // Validate vegani
    if (teamData.vegani && teamData.vegani.trim()) {
      const vegani = parseInt(teamData.vegani);
      if (isNaN(vegani) || vegani < 0 || vegani > 20) {
        newErrors.vegani = 'Numero vegani deve essere tra 0 e 20';
      }
    }

    // Validate celiaci
    if (teamData.celiaci && teamData.celiaci.trim()) {
      const celiaci = parseInt(teamData.celiaci);
      if (isNaN(celiaci) || celiaci < 0 || celiaci > 20) {
        newErrors.celiaci = 'Numero celiaci deve essere tra 0 e 20';
      }
    }

    // Validate altreIntolleranze length
    if (teamData.altreIntolleranze && teamData.altreIntolleranze.length > 500) {
      newErrors.altreIntolleranze = 'Il campo non può superare 500 caratteri';
    }

    // Validate players count and gender distribution
    const minPlayers = torneo?.preiscrizione ? 0 : 7;

    if (players.length === 0) {
      newErrors.players = 'Aggiungi almeno un giocatore';
    } else if (players.length < minPlayers) {
      newErrors.players = `Sono richiesti almeno ${minPlayers} giocatori`;
    } else {
      const playersWithGender = players.filter(player => player.sesso && player.sesso.trim());
      const maleCount = playersWithGender.filter(player => player.sesso === 'M').length;
      const femaleCount = playersWithGender.filter(player => player.sesso === 'F').length;
      
      if (playersWithGender.length >= 7) {
        if (maleCount < 2) {
          newErrors.players = 'Sono richiesti almeno 2 giocatori maschi';
        } else if (femaleCount < 2) {
          newErrors.players = 'Sono richieste almeno 2 giocatrici femmine';
        }
      }
    }

    // Validate individual player fields
    const censitiEnabled = torneo?.censiti === 'true' || torneo?.censiti === 'SI';

    players.forEach(player => {
      const baseKey = `player_${player.id}`;
      const isNewPlayer = player.isNew || !isEditMode;

      // 1234 e' il passepartout: si usa quando il codice non si conosce o i dati non tornano, e
      // sospende sia il controllo sul codice sia quello sul codice fiscale
      const passepartout = player.codice.trim() === CODICE_PASSEPARTOUT;

      // Validazione codice o CF - dipende dal flag censiti del torneo
      if (censitiEnabled) {
        // Se censiti è true, richiedi il codice Scout
        if (!player.codice.trim()) {
          newErrors[`${baseKey}_codice`] = 'Codice obbligatorio';
        } else if (isNaN(parseInt(player.codice.trim()))) {
          newErrors[`${baseKey}_codice`] = 'Il codice deve essere un numero';
        } else if (!passepartout && player.codice.trim().length < CODICE_CIFRE_MINIME) {
          newErrors[`${baseKey}_codice`] =
            `Il codice deve avere almeno ${CODICE_CIFRE_MINIME} cifre. Se non lo conosci usa ${CODICE_PASSEPARTOUT}`;
        }
      } else if (!player.codiceFiscale.trim() && !passepartout) {
        // Se censiti è false, richiedi il codice fiscale
        newErrors[`${baseKey}_codiceFiscale`] = 'Codice fiscale obbligatorio';
      }

      // Il codice fiscale, quando c'e', deve corrispondere ai dati anagrafici digitati: formato,
      // carattere di controllo, cognome, nome, data di nascita e sesso. Il comune di nascita non
      // e' verificabile senza chiederlo, e resta fuori.
      if (!passepartout && player.codiceFiscale.trim()) {
        const erroreCf = verificaCodiceFiscale(player.codiceFiscale, {
          nome: player.nome,
          cognome: player.cognome,
          dataNascita: player.dataNascita,
          sesso: player.sesso
        });
        if (erroreCf) {
          newErrors[`${baseKey}_codiceFiscale`] = erroreCf;
        }
      }
      
      // Validazione nome - sempre obbligatorio per tutti i giocatori
      if (!player.nome.trim()) {
        newErrors[`${baseKey}_nome`] = 'Nome obbligatorio';
      } else if (player.nome.trim().length > 20) {
        newErrors[`${baseKey}_nome`] = 'Nome non può superare 20 caratteri';
      }
      
      // Validazione cognome - sempre obbligatorio per tutti i giocatori
      if (!player.cognome.trim()) {
        newErrors[`${baseKey}_cognome`] = 'Cognome obbligatorio';
      } else if (player.cognome.trim().length > 20) {
        newErrors[`${baseKey}_cognome`] = 'Cognome non può superare 20 caratteri';
      }
      
      // Validazione sesso - sempre obbligatorio per tutti i giocatori
      if (!player.sesso) {
        newErrors[`${baseKey}_sesso`] = 'Sesso obbligatorio';
      }
      
      // Validazione data nascita - sempre obbligatorio per tutti i giocatori
      if (!player.dataNascita) {
        newErrors[`${baseKey}_dataNascita`] = 'Data nascita obbligatoria';
      } else {
        const birthDate = new Date(player.dataNascita);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        if (age < 11) {
          newErrors[`${baseKey}_dataNascita`] = 'Il giocatore deve avere almeno 16 anni';
        }
      }
      
      // Validazione gruppo - sempre obbligatorio per tutti i giocatori
      if (!player.gruppo.trim()) {
        newErrors[`${baseKey}_gruppo`] = 'Gruppo obbligatorio';
      }
      
      // Validazione numero maglia - sempre obbligatorio
      if (!player.numeroMaglia.trim()) {
        newErrors[`${baseKey}_numeroMaglia`] = 'Numero maglia obbligatorio';
      } else {
        const jerseyNum = parseInt(player.numeroMaglia);
        if (isNaN(jerseyNum) || jerseyNum < 0 || jerseyNum > 99) {
          newErrors[`${baseKey}_numeroMaglia`] = 'Numero maglia deve essere tra 0 e 99';
        }
      }
    });

    // Check for duplicate player codes - solo se censiti è abilitato
    if (censitiEnabled) {
      const playerCodes = {};
      const validPlayerCodes = players.filter(player =>
        player.codice.trim() &&
        !isNaN(parseInt(player.codice.trim()))
      );

      validPlayerCodes.forEach(player => {
        const code = parseInt(player.codice.trim());

        if (playerCodes[code]) {
          playerCodes[code].push(player.id);
        } else {
          playerCodes[code] = [player.id];
        }
      });

      Object.entries(playerCodes).forEach(([code, playerIds]) => {
        if (playerIds.length > 1) {
          playerIds.forEach(playerId => {
            const errorKey = `player_${playerId}_codice`;
            if (!newErrors[errorKey]) {
              newErrors[errorKey] = `Codice ${code} già utilizzato`;
            }
          });
        }
      });
    } else {
      // Check for duplicate fiscal codes
      const fiscalCodes = {};
      const validFiscalCodes = players.filter(player =>
        player.codiceFiscale.trim()
      );

      validFiscalCodes.forEach(player => {
        const cf = player.codiceFiscale.trim().toUpperCase();

        if (fiscalCodes[cf]) {
          fiscalCodes[cf].push(player.id);
        } else {
          fiscalCodes[cf] = [player.id];
        }
      });

      Object.entries(fiscalCodes).forEach(([cf, playerIds]) => {
        if (playerIds.length > 1) {
          playerIds.forEach(playerId => {
            const errorKey = `player_${playerId}_codiceFiscale`;
            if (!newErrors[errorKey]) {
              newErrors[errorKey] = `Codice fiscale ${cf} già utilizzato`;
            }
          });
        }
      });
    }

    // Check for duplicate jersey numbers
    const jerseyNumbers = {};
    const validPlayers = players.filter(player => 
      player.numeroMaglia.trim() && 
      !isNaN(parseInt(player.numeroMaglia)) &&
      parseInt(player.numeroMaglia) >= 1 && 
      parseInt(player.numeroMaglia) <= 99
    );

    validPlayers.forEach(player => {
      const jerseyNum = player.numeroMaglia.trim();
      
      if (jerseyNumbers[jerseyNum]) {
        jerseyNumbers[jerseyNum].push(player.id);
      } else {
        jerseyNumbers[jerseyNum] = [player.id];
      }
    });

    Object.entries(jerseyNumbers).forEach(([jerseyNum, playerIds]) => {
      if (playerIds.length > 1) {
        playerIds.forEach(playerId => {
          const errorKey = `player_${playerId}_numeroMaglia`;
          if (!newErrors[errorKey]) {
            newErrors[errorKey] = `Numero ${jerseyNum} già utilizzato`;
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePreiscrizioneSubmit = async () => {
    if (isEditingBlocked) {
      setSubmitError('Il termine per le modifiche è scaduto. Contatta l\'organizzazione.');
      return;
    }

    // Validazione solo dati squadra per preiscrizione
    const newErrors = {};

    // Validate team data
    if (!teamData.maglia.trim()) {
      newErrors.maglia = 'Nome squadra obbligatorio';
    }

    if (!teamData.contatto.trim()) {
      newErrors.contatto = 'Contatto obbligatorio';
    } else if (!/^[+]?[0-9\s\-\(\)]{10,}$/.test(teamData.contatto)) {
      newErrors.contatto = 'Formato telefono non valido';
    }

    if (!teamData.gruppoScout) {
      newErrors.gruppoScout = 'Gruppo scout obbligatorio';
    }

    // Valida campi cibo se abilitati
    if (torneo?.cibo) {
      if (teamData.accompagnatori && teamData.accompagnatori.trim()) {
        const accompagnatori = parseInt(teamData.accompagnatori);
        if (isNaN(accompagnatori) || accompagnatori < 0 || accompagnatori > 10) {
          newErrors.accompagnatori = 'Numero accompagnatori deve essere tra 0 e 10';
        }
      }

      if (teamData.vegetariani && teamData.vegetariani.trim()) {
        const vegetariani = parseInt(teamData.vegetariani);
        if (isNaN(vegetariani) || vegetariani < 0 || vegetariani > 20) {
          newErrors.vegetariani = 'Numero vegetariani deve essere tra 0 e 20';
        }
      }

      if (teamData.vegani && teamData.vegani.trim()) {
        const vegani = parseInt(teamData.vegani);
        if (isNaN(vegani) || vegani < 0 || vegani > 20) {
          newErrors.vegani = 'Numero vegani deve essere tra 0 e 20';
        }
      }

      if (teamData.celiaci && teamData.celiaci.trim()) {
        const celiaci = parseInt(teamData.celiaci);
        if (isNaN(celiaci) || celiaci < 0 || celiaci > 20) {
          newErrors.celiaci = 'Numero celiaci deve essere tra 0 e 20';
        }
      }

      if (teamData.altreIntolleranze && teamData.altreIntolleranze.length > 500) {
        newErrors.altreIntolleranze = 'Il campo non può superare 500 caratteri';
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setSubmitError('Correggi gli errori nei dati squadra prima di inviare');
      return;
    }

    // Procedi con il submit
    setSubmitting(true);
    setSubmitError('');

    try {
      const ciboEnabled = torneo?.cibo !== false;

      const formData = {
        nome: teamData.maglia.trim(),
        torneo: parseInt(torneoId),
        logo: teamData.logo?.base64 || null,
        contattoMail: null,
        cellulare: teamData.contatto.trim(),
        hash: teamData.hash ?? '',
        preiscrizione: true,
        gruppoScout: teamData.gruppoScout,
        accompagnatori: ciboEnabled && teamData.accompagnatori ? parseInt(teamData.accompagnatori) : 0,
        vegetariani: ciboEnabled && teamData.vegetariani ? parseInt(teamData.vegetariani) : 0,
        vegani: ciboEnabled && teamData.vegani ? parseInt(teamData.vegani) : 0,
        celiaci: ciboEnabled && teamData.celiaci ? parseInt(teamData.celiaci) : 0,
        altreIntolleranze: ciboEnabled && teamData.altreIntolleranze?.trim() ? teamData.altreIntolleranze.trim() : null,
        giocatori: [] // Array vuoto per preiscrizione
      };

      // In modalità modifica, aggiungi l'ID della squadra
      if (isEditMode && originalSquadraData) {
        formData.id = originalSquadraData.id;
      }

      console.log('Invio preiscrizione:', JSON.stringify(formData, null, 2));

      const response = isEditMode
        ? await API.iscrizioni.aggiorna(formData, codiceAdmin)
        : await API.iscrizioni.crea(formData);

      if (response.isSuccess && response.data) {
        navigate('/tornei/iscrizioni', {
          state: {
            message: 'Preiscrizione completata con successo!',
            type: 'success',
            iscrizioneId: response.data.id
          }
        });
      } else {
        setSubmitError(response.errorMsg || 'Errore durante la preiscrizione. Riprova più tardi.');
      }

    } catch (error) {
      console.error('Errore nella preiscrizione:', error);
      setSubmitError('Errore di connessione. Verifica la tua connessione e riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditingBlocked) {
      setSubmitError('Il termine per le modifiche è scaduto. Contatta l\'organizzazione.');
      return;
    }

    if (!validateForm()) {
      setSubmitError('Correggi gli errori nel modulo prima di inviare');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    
    try {
      // Se cibo è false, forza i valori a 0/null
      const ciboEnabled = torneo?.cibo !== false;

      const formData = {
        nome: teamData.maglia.trim(),
        torneo: parseInt(torneoId),
        logo: teamData.logo?.base64 || null,
        contattoMail: null,
        cellulare: teamData.contatto.trim(),
        hash: teamData.hash ?? '',
        preiscrizione: false, // Registrazione completa con giocatori
        gruppoScout: teamData.gruppoScout,
        accompagnatori: ciboEnabled && teamData.accompagnatori ? parseInt(teamData.accompagnatori) : 0,
        vegetariani: ciboEnabled && teamData.vegetariani ? parseInt(teamData.vegetariani) : 0,
        vegani: ciboEnabled && teamData.vegani ? parseInt(teamData.vegani) : 0,
        celiaci: ciboEnabled && teamData.celiaci ? parseInt(teamData.celiaci) : 0,
        altreIntolleranze: ciboEnabled && teamData.altreIntolleranze?.trim() ? teamData.altreIntolleranze.trim() : null,
        giocatori: players.map(player => ({
          ...(player.id && !player.isNew ? { id: player.id } : {}), // Include ID solo per giocatori esistenti
          cod: player.codice.trim() ? parseInt(player.codice.trim()) : null,
          nome: player.nome.trim(),
          cognome: player.cognome.trim(),
          dataNascita: player.dataNascita,
          sesso: player.sesso,
          gruppo: player.gruppo.trim(),
          nomeMaglia: player.maglia.trim() || teamData.maglia.trim(),
          numeroMaglia: parseInt(player.numeroMaglia),
          codiceFiscale: player.codiceFiscale.trim() ? player.codiceFiscale.trim().toUpperCase() : null
        }))
      };

      // In modalità modifica, aggiungi l'ID della squadra
      if (isEditMode && originalSquadraData) {
        formData.id = originalSquadraData.id;
      }

      console.log('Invio dati:', JSON.stringify(formData, null, 2));
      
      const response = isEditMode
        ? await API.iscrizioni.aggiorna(formData, codiceAdmin)
        : await API.iscrizioni.crea(formData);

      if (response.isSuccess && response.data) {
        navigate('/tornei/iscrizioni', {
          state: {
            message: isEditMode ? 'Squadra aggiornata con successo!' : 'Iscrizione completata con successo!',
            type: 'success',
            iscrizioneId: response.data.id
          }
        });
      } else {
        setSubmitError(response.errorMsg || `Errore durante ${isEditMode ? 'l\'aggiornamento' : 'l\'iscrizione'}. Riprova più tardi.`);
      }
      
    } catch (error) {
      console.error('Errore nell\'operazione:', error);
      setSubmitError('Errore di connessione. Verifica la tua connessione e riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/tornei/iscrizioni');
  };

  if (loading) {
    return (
      <div className="inscription-form-page">
        <Header />
        <main className="inscription-form-main">
          <div className="inscription-form-container">
            <div className="inscription-loading-container">
              <div className="inscription-loading-spinner"></div>
              <p>Caricamento {isEditMode ? 'dati squadra' : 'dettagli torneo'}...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="inscription-form-page">
        <Header />
        <main className="inscription-form-main">
          <div className="inscription-form-container">
            <div className="inscription-loading-container">
              <h2>{iscrizioniChiuse ? 'Iscrizioni chiuse' : 'Errore'}</h2>
              <p>{error}</p>
              <button 
                onClick={() => navigate('/tornei/iscrizioni')}
                className="inscription-btn-cancel"
              >
                Torna alla lista tornei
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="inscription-form-page">
      <Header />
      
      <main className="inscription-form-main">
        <div className="inscription-form-container">

          <form onSubmit={handleSubmit}>
            <div className="inscription-top-sections">
              <TeamData
                teamData={teamData}
                errors={errors}
                onTeamDataChange={handleTeamDataChange}
                onLogoUpload={handleLogoUpload}
                onRemoveLogo={removeLogoHandler}
                isEditMode={isEditMode}
                mostraCibo={torneo?.cibo !== false}
                preiscrizione={torneo?.preiscrizione === true}
                onPreiscrizioneSubmit={handlePreiscrizioneSubmit}
                isSubmitting={submitting}
                gruppiScout={gruppiScout}
                bloccato={isEditingBlocked}
              />

              <TournamentData torneo={torneo} />
            </div>

            <PlayerData
              players={players}
              errors={errors}
              onAddPlayer={addPlayer}
              onRemovePlayer={removePlayer}
              onUpdatePlayer={updatePlayer}
              onPlayerCodeBlur={handlePlayerCodeBlur}
              isEditMode={isEditMode}
              squadraPreiscrizione={squadraPreiscrizione}
              censiti={torneo?.censiti === 'true' || torneo?.censiti === 'SI'}
              gruppiScout={gruppiScout}
              bloccato={isEditingBlocked}
            />

            {isAdmin && isEditDeadlinePassed && (
              <div className="inscription-form-section">
                <div className="inscription-section-content">
                  <div className="inscription-error-message" style={{
                    backgroundColor: '#ebf8ff',
                    border: '1px solid #bee3f8',
                    borderRadius: '4px',
                    padding: '1rem',
                    textAlign: 'center',
                    fontSize: '0.9rem'
                  }}>
                    <i className="fas fa-user-shield" style={{marginRight: '0.5rem'}}></i>
                    Modalità organizzazione: le modifiche restano possibili fino alla chiusura dell'edizione.
                  </div>
                </div>
              </div>
            )}

            {isEditingBlocked && (
              <div className="inscription-form-section">
                <div className="inscription-section-content">
                  <div className="inscription-error-message" style={{
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '4px',
                    padding: '1rem',
                    textAlign: 'center',
                    fontSize: '0.9rem'
                  }}>
                    <i className="fas fa-lock" style={{marginRight: '0.5rem'}}></i>
                    Il termine per le modifiche è scaduto. Contatta l'organizzazione per apportare cambiamenti.
                  </div>
                </div>
              </div>
            )}

            {submitError && (
              <div className="inscription-form-section">
                <div className="inscription-section-content">
                  <div className="inscription-error-message" style={{
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '4px',
                    padding: '1rem',
                    textAlign: 'center',
                    fontSize: '0.9rem'
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{marginRight: '0.5rem'}}></i>
                    {submitError}
                  </div>
                </div>
              </div>
            )}

            <div className="inscription-form-actions">
              <button
                type="submit"
                className="inscription-btn-submit"
                disabled={submitting || isEditingBlocked}
              >
                {submitting && <div className="inscription-loading-spinner"></div>}
                {submitting
                  ? (isEditMode ? 'Aggiornamento...' : 'Invio in corso...')
                  : (isEditMode
                      ? (squadraPreiscrizione ? 'Iscrivi Squadra' : 'Aggiorna Squadra')
                      : 'Invia Iscrizione')
                }
              </button>
            </div>
          </form>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default IscrizioneForm;